import React, { useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  Alert,
  Typography
} from '@mui/material';
import { CalendarMonth, EventAvailable } from '@mui/icons-material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { differenceInCalendarDays, format } from 'date-fns';
import { Session } from '../../types/sessions';
import { sessionsApi } from '../../services/sessions/sessionsApi';

interface SessionFormDialogProps {
  open: boolean;
  initial?: Session | null;       // null/undefined = create mode
  onClose: () => void;
  onSaved: (session: Session) => void;
}

interface FormValues {
  name: string;
  startDate: Date | null;
  endDate: Date | null;
}

const validationSchema = yup.object({
  name: yup.string().trim().required('Session name is required').max(80, 'Keep it concise'),
  startDate: yup.date().nullable().required('Pick a start date').typeError('Invalid date'),
  endDate: yup.date().nullable().required('Pick an end date').typeError('Invalid date')
    .min(yup.ref('startDate'), 'End date must be on or after start date')
});

const suggestNameFromDate = (date: Date) => {
  const y = date.getFullYear();
  return `${y}-${y + 1}`;
};

const SessionFormDialog: React.FC<SessionFormDialogProps> = ({ open, initial, onClose, onSaved }) => {
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = Boolean(initial);

  const initialValues = useMemo<FormValues>(() => ({
    name: initial?.name ?? '',
    startDate: initial?.startDate ? new Date(initial.startDate) : null,
    endDate: initial?.endDate ? new Date(initial.endDate) : null
  }), [initial]);

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = {
          name: values.name.trim(),
          startDate: values.startDate!.toISOString(),
          endDate: values.endDate!.toISOString()
        };
        const saved = isEdit
          ? await sessionsApi.update(initial!._id, payload)
          : await sessionsApi.create(payload);
        enqueueSnackbar(isEdit ? 'Session updated' : 'Session created — set as Active when ready', {
          variant: 'success'
        });
        onSaved(saved);
        onClose();
      } catch (err: any) {
        enqueueSnackbar(err?.message || 'Could not save session', { variant: 'error' });
      } finally {
        setSubmitting(false);
      }
    }
  });

  // Quietly suggest a name when admin picks a start date in create mode.
  useEffect(() => {
    if (!isEdit && formik.values.startDate && !formik.values.name) {
      formik.setFieldValue('name', suggestNameFromDate(formik.values.startDate));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.startDate, isEdit]);

  const lengthInDays = formik.values.startDate && formik.values.endDate
    ? differenceInCalendarDays(formik.values.endDate, formik.values.startDate) + 1
    : null;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <form onSubmit={formik.handleSubmit} noValidate>
          <DialogTitle component="div" sx={{ pb: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <EventAvailable color="primary" fontSize="small" />
              <Typography variant="h6" fontWeight={600}>
                {isEdit ? 'Edit session' : 'New academic session'}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {isEdit
                ? 'Update the session details. Changes apply immediately.'
                : 'Sessions track the school’s academic year. You can activate it after creation.'}
            </Typography>
          </DialogTitle>

          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2.5} sx={{ mt: 0.5 }}>
              <TextField
                name="name"
                label="Session name"
                placeholder="e.g. 2024-2025"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={(formik.touched.name && formik.errors.name) || 'Shown across the app — pick something recognisable.'}
                autoFocus
                fullWidth
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <DatePicker
                  label="Start date"
                  value={formik.values.startDate}
                  onChange={(date) => formik.setFieldValue('startDate', date)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      onBlur: () => formik.setFieldTouched('startDate', true),
                      error: formik.touched.startDate && Boolean(formik.errors.startDate),
                      helperText: formik.touched.startDate && (formik.errors.startDate as string)
                    }
                  }}
                />
                <DatePicker
                  label="End date"
                  value={formik.values.endDate}
                  onChange={(date) => formik.setFieldValue('endDate', date)}
                  minDate={formik.values.startDate ?? undefined}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      onBlur: () => formik.setFieldTouched('endDate', true),
                      error: formik.touched.endDate && Boolean(formik.errors.endDate),
                      helperText: formik.touched.endDate && (formik.errors.endDate as string)
                    }
                  }}
                />
              </Stack>

              {lengthInDays != null && (
                <Alert icon={<CalendarMonth fontSize="inherit" />} severity="info" variant="outlined" sx={{ border: 'none', bgcolor: 'transparent', py: 0 }}>
                  <Typography variant="body2" color="text.secondary">
                    {format(formik.values.startDate!, 'MMM d, yyyy')} → {format(formik.values.endDate!, 'MMM d, yyyy')}
                    <Typography component="span" sx={{ mx: 1, color: 'text.disabled' }}>·</Typography>
                    <strong>{lengthInDays}</strong> {lengthInDays === 1 ? 'day' : 'days'}
                  </Typography>
                </Alert>
              )}
            </Stack>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={onClose} disabled={formik.isSubmitting}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={formik.isSubmitting}
              disableElevation
            >
              {isEdit ? 'Save changes' : 'Create session'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </LocalizationProvider>
  );
};

export default SessionFormDialog;
